import { Modal, Button } from "react-bootstrap";
import {
  ChevronLeft,
  ChevronRight,
  Trash,
  X,
  ArrowsFullscreen,
  ArrowsAngleContract,
  Eye,
} from "react-bootstrap-icons";
import { useState, useEffect, useRef } from "react";
import { MediaItem } from "../lib/get-file-type";
import { getCachedBlob } from "../lib/blob-cache-persistent";
import { useLazyFileLoad } from "../hooks";

interface MediaModalProps {
  media: MediaItem[];
  currentIndex: number | null;
  isOpen: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDelete?: (index: number) => void;
  goToMessage?: (msgId: string) => void;
}

export const MediaModal: React.FC<MediaModalProps> = ({
  media,
  isOpen,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  onDelete,
  goToMessage,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const textRef = useRef<HTMLPreElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [textContent, setTextContent] = useState<string | null>(null);

  const currentItem = media[currentIndex ?? 0] || media[0];
  const showNavigation = media.length > 1;
  const isImage = currentItem?.type === "image";
  const isVideo = currentItem?.type === "video";
  const isText = (currentItem as any)?.type === "text";

  useEffect(() => {
    if (!isOpen || !currentItem) {
      setCurrentSrc(null);
      setTextContent(null);
      setLoading(false);
      return;
    }

    const loadMedia = async () => {
      try {
        setLoading(true);

        if (isText) {
          let blob: Blob;

          if (currentItem.url.startsWith("blob:")) {
            const response = await fetch(currentItem.url);
            blob = await response.blob();
          } else {
            blob = (await getCachedBlob(currentItem.url)).blob;
          }

          const text = await blob.text();
          setTextContent(text);
          setCurrentSrc(null);
        } else {
          if (currentItem.url.startsWith("blob:")) {            
            setCurrentSrc(currentItem.url);
          } else {
            const blobUrl = (await getCachedBlob(currentItem.url)).url;
            setCurrentSrc(blobUrl);
          }
          setTextContent(null);
        }
      } catch (error) {
        console.error("Failed to load media:", error);
        setCurrentSrc(null);
        setTextContent("Ошибка загрузки файла");
      } finally {
        setLoading(false);
      }
    };

    loadMedia();
  }, [isOpen, currentIndex, currentItem?.url]);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    } else {
      const el = isImage
        ? imgRef.current
        : isVideo
          ? videoRef.current
          : textRef.current;
      if (el) {
        el.requestFullscreen().catch(console.error);
      }
    }
  };

  if (!isOpen || media.length === 0) {
    return null;
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      backdrop={true}
      keyboard={true}
      className="media-modal"
      style={{ zIndex: 1060 }}
    >
      <div
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          zIndex: 1051,
        }}
      >
        <Button
          className="d-none d-md-block"
          variant="link"
          onClick={toggleFullscreen}
          style={{
            color: "white",
            padding: "6px",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            background: "rgba(0, 0, 0, 0.4)",
            border: "none",
          }}
        >
          {isFullscreen ? (
            <ArrowsAngleContract size={14} style={{marginTop: -2}} />
          ) : (
            <ArrowsFullscreen size={14} style={{marginTop: -2}} />
          )}
        </Button>

        {onDelete && (
          <Button
            variant="link"
            onClick={() => onDelete(currentIndex || 0)}
            style={{
              color: "#ff6b6b",
              padding: "6px",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0, 0, 0, 0.4)",
              border: "none",
            }}
          >
            <Trash size={16} />
          </Button>
        )}

        {goToMessage && (
          <Button
            variant="link"
            onClick={() => currentItem.msgId && goToMessage(currentItem.msgId)}
            title="Показать в чате"
            style={{
              color: "white",
              padding: "6px",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0, 0, 0, 0.4)",
              border: "none",
            }}
          >
            <Eye size={16} />
          </Button>
        )}

        <Button
          variant="link"
          onClick={onClose}
          style={{
            color: "white",
            padding: "6px",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.4)",
            border: "none",
          }}
        >
          <X size={16} />
        </Button>
      </div>

      {showNavigation && (
        <div
          style={{
            position: "absolute",
            bottom: "12px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            color: "#111",
            fontSize: "14px",
            zIndex: 1051,
          }}
        >
          {(currentIndex || 0) + 1} из {media.length}
        </div>
      )}

      {/* Кнопки навигации */}
      {showNavigation && (
        <>
          <Button
            variant="link"
            onClick={onPrev}
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              background: "rgba(0, 0, 0, 0.4)",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1050,
            }}
          >
            <ChevronLeft size={20} />
          </Button>

          <Button
            variant="link"
            onClick={onNext}
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              background: "rgba(0, 0, 0, 0.4)",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1050,
            }}
          >
            <ChevronRight size={20} />
          </Button>
        </>
      )}

      <Modal.Body
        className="rounded-5"
        onContextMenu={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "black",
          position: "relative",
          padding: 0,
          height: "90vh",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            color: "white",
          }}
        >
          {loading ? (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 1)",
                zIndex: 10,
              }}
            >
              <div className="spinner-border" role="status" style={{ width: "3rem", height: "3rem", color: "white" }}>
                <span className="visually-hidden">Загрузка...</span>
              </div>
            </div>
          ) : textContent ? (
            <pre
              className="scrollbar-custom-y"
              ref={textRef}
              onWheel={(e) => {
                const el = e.currentTarget;
                const isScrollable = el.scrollHeight > el.clientHeight;
                if (isScrollable) {
                  e.stopPropagation();
                }
              }}
              style={{
                color: "#111",
                backgroundColor: "#fff",
                padding: "20px",
                width: "100%",
                height: "100%",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                margin: 0,
              }}
            >
              {textContent}
            </pre>
          ) : currentItem?.type === "other" ? (
            <div
              style={{
                color: "#111",
                textAlign: "center",
                padding: "40px",
                fontSize: "1.1rem",
                borderRadius: "12px",
                lineHeight: 1.5,
              }}
            >
              Этот тип файла нельзя просмотреть в приложении.
              <br />
              Скачайте файл, чтобы открыть его на вашем устройстве.
            </div>
          ) : currentSrc ? (
            currentItem?.type === "image" ? (
              <img
                ref={imgRef}
                src={currentSrc}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  visibility: loading ? "hidden" : "visible",
                }}
              />
            ) : (
              <video
                ref={videoRef}
                src={currentSrc}
                controls
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  outline: "none",
                  visibility: loading ? "hidden" : "visible",
                }}
              />
            )
          ) : (
            <div style={{ color: "red", textAlign: "center", padding: "20px" }}>
              Ошибка загрузки файла
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
